/* =========================================================
   Diesel wishlist — localStorage only. No app, no backend.

   Mirrors the behaviour observed on uk.diesel.com (which runs a
   server-side SFCC Wishlist controller we can't port to Shopify):
   saves work anonymously with no login gate, and persist across
   reloads. Here the list lives entirely in the browser.

   Storage: localStorage['diesel_wishlist'] = ["handle-a","handle-b"]
   Product handles, not variant ids — the wishlist is colour//style
   level, and the shopper picks a size on the PDP.

   Public API (window.DieselWishlist):
     getAll() -> [handle]      has(handle) -> bool
     count()  -> number        add/remove/toggle(handle) -> bool (saved?)
     clear()

   Fires 'diesel:wishlist:change' on document with
   detail { handles, count }. Also listens to the cross-tab `storage`
   event so every open tab stays in sync.

   Degrades quietly when localStorage is unavailable (private mode,
   blocked cookies): falls back to an in-memory list for the tab and
   never throws.
   ========================================================= */
(function () {
  'use strict';

  var KEY = 'diesel_wishlist';
  var EVENT = 'diesel:wishlist:change';

  /* ---------- storage layer ---------- */

  // In-memory fallback so the UI still behaves within a single page
  // view when localStorage is blocked.
  var memory = [];

  var usable = (function () {
    try {
      var probe = '__dsl_wl_probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch (e) {
      return false;
    }
  })();

  function read() {
    if (!usable) return memory.slice();
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      // Defend against hand-edited / corrupted values.
      return arr.filter(function (h) {
        return typeof h === 'string' && h.length > 0;
      });
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    memory = list.slice();
    if (!usable) return;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      // Quota exceeded or storage blocked mid-session — the in-memory
      // copy still serves this tab.
    }
  }

  function emit() {
    var list = read();
    document.dispatchEvent(
      new CustomEvent(EVENT, { detail: { handles: list, count: list.length } })
    );
  }

  /* ---------- public API ---------- */

  function getAll() {
    return read();
  }

  function has(handle) {
    if (!handle) return false;
    return read().indexOf(handle) !== -1;
  }

  function count() {
    return read().length;
  }

  function add(handle) {
    if (!handle) return false;
    var list = read();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      write(list);
      emit();
    }
    return true;
  }

  function remove(handle) {
    if (!handle) return false;
    var list = read();
    var i = list.indexOf(handle);
    if (i !== -1) {
      list.splice(i, 1);
      write(list);
      emit();
    }
    return false;
  }

  function toggle(handle) {
    return has(handle) ? remove(handle) : add(handle);
  }

  function clear() {
    write([]);
    emit();
  }

  /* ---------- UI binding ---------- */

  // Every heart across the theme carries data-wishlist-handle. The
  // markup and the [aria-pressed='true'] filled styling already existed
  // per surface (.diesel-wishlist on PLP cards + PDP, .mrecs-wishlist in
  // carousels, .diesel-recs-wishlist in recommendations) — this only
  // drives the state, it does not add buttons.
  function syncHearts() {
    var saved = read();
    var buttons = document.querySelectorAll('[data-wishlist-handle]');
    Array.prototype.forEach.call(buttons, function (btn) {
      var handle = btn.getAttribute('data-wishlist-handle');
      var on = saved.indexOf(handle) !== -1;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = btn.getAttribute(on ? 'data-label-remove' : 'data-label-add');
      if (label) btn.setAttribute('aria-label', label);
    });
  }

  function syncBadges() {
    var n = count();
    var badges = document.querySelectorAll('[data-wishlist-count]');
    Array.prototype.forEach.call(badges, function (el) {
      el.textContent = n;
      // Hidden at zero, matching the cart badge's behaviour.
      if (n > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  function syncAll() {
    syncHearts();
    syncBadges();
  }

  // Delegated so it survives DOM replacement — the PDP colour swap
  // replaces the whole <product-info>, and quick-add panels re-render.
  function onClick(e) {
    var btn = e.target.closest ? e.target.closest('[data-wishlist-handle]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(btn.getAttribute('data-wishlist-handle'));
  }

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener(EVENT, syncAll);

    // Cross-tab: another tab wrote the key, mirror it here.
    window.addEventListener('storage', function (e) {
      if (e.key === KEY) emit();
    });

    // The colour swap swaps in a fresh <product-info> whose heart
    // carries the swapped-to product's handle — re-sync after it lands.
    document.addEventListener('dsl:swatch:swapped', syncAll);

    syncAll();
  }

  window.DieselWishlist = {
    getAll: getAll,
    has: has,
    count: count,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear,
    // Re-apply saved state to hearts + badges in the current DOM. Call after
    // injecting new hearts (e.g. the wishlist page appends fetched cards).
    // Does NOT dispatch the change event, so it can't loop with listeners.
    syncUI: syncAll,
    isPersistent: function () {
      return usable;
    },
    STORAGE_KEY: KEY,
    CHANGE_EVENT: EVENT,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
